// Shared implementation copied verbatim to LicensePortal and Website. No PAN,
// CVC or Stripe secrets are read by RingOffice JavaScript or stored in the browser.
export type StripeAction = { provider: 'stripe'; type: 'authenticate' | 'setup'; clientSecret: string; publicKey: string; integrationId: string };
type StripeError = { message?: string };
type StripeElement = { mount(selector: string): void; destroy(): void };
type StripeElements = { create(type: string, options?: object): StripeElement; submit(): Promise<{error?: StripeError}>; update(options: object): void };
type StripeClient = {
  elements(options: object): StripeElements;
  createConfirmationToken(options: object): Promise<{ error?: StripeError; confirmationToken?: { id: string } }>;
  handleNextAction(options: {clientSecret: string}): Promise<{error?: StripeError}>;
};
type StripeWindow = Window & { Stripe?: (key: string) => StripeClient };
let scriptPromise: Promise<void> | undefined;
export function loadStripeScript(): Promise<void> {
  if ((window as StripeWindow).Stripe) return Promise.resolve();
  if (!scriptPromise) scriptPromise = new Promise<void>((resolve, reject) => {
    const script = document.createElement('script');
    script.src = 'https://js.stripe.com/dahlia/stripe.js'; script.async = true;
    script.onload = () => resolve();
    script.onerror = () => { script.remove(); scriptPromise = undefined; reject(new Error('Secure payment fields could not load. Please try again.')); };
    document.head.appendChild(script);
  });
  return scriptPromise;
}
type StripeBilling = { line1?: string | null; line2?: string | null; city?: string | null; region?: string | null; postalCode?: string | null; countryCode?: string | null; country?: string | null; email?: string | null; phone?: string | null };
export type StripeCheckout = { provider: 'stripe'; token(name?: string, save?: boolean, billing?: StripeBilling): Promise<string>; destroy(): void };
export async function mountStripeCheckout(config: {publicKey?: string | null; currency?: string | null}, ids: {cardNumber: string; expiry: string; cvv: string}, options: {mode: 'payment'|'setup'; amount?: number; currency?: string; save?: boolean; externalBilling?: boolean}): Promise<StripeCheckout> {
  await loadStripeScript();
  const factory = (window as StripeWindow).Stripe;
  if (!factory || !/^pk_(test|live)_/.test(config.publicKey || '')) throw new Error('Stripe payment configuration is incomplete.');
  const stripe = factory(config.publicKey!);
  const currency = String(options.currency || config.currency || '').toLowerCase();
  if (!['cad','usd'].includes(currency)) throw new Error('Payment currency is unavailable.');
  if (options.mode === 'payment' && (!Number.isSafeInteger(options.amount) || Number(options.amount) <= 0)) throw new Error('Review the order total before paying.');
  const elements = stripe.elements({ mode: options.mode, currency, paymentMethodCreation: 'manual', paymentMethodTypes:['card'],
    ...(options.mode === 'payment' ? {amount: options.amount} : {}),
    ...(options.save && options.mode === 'payment' ? {setupFutureUsage:'off_session'} : {}),
    appearance:{ theme:'stripe', variables:{colorPrimary:'#008ccb',fontFamily:'Arial, sans-serif',borderRadius:'10px'} },
  });
  const host = document.getElementById(ids.cardNumber);
  if (!host) throw new Error('Secure payment form is not ready.');
  const styles=host.getAttribute('style');
  host.style.height='auto';host.style.minHeight='180px';host.style.padding='0';host.style.border='0';
  const hidden: {element:HTMLElement;display:string;priority:string}[]=[];
  for (const id of [ids.expiry,ids.cvv]) {
    const field = document.getElementById(id);
    const wrapper = field?.closest('label') || field?.parentElement;
    if (wrapper instanceof HTMLElement && !wrapper.contains(host)) {
      hidden.push({element:wrapper,display:wrapper.style.getPropertyValue('display'),priority:wrapper.style.getPropertyPriority('display')});
      wrapper.style.setProperty('display','none','important');
    }
  }
  const label = host.previousElementSibling;
  const labelText = label?.textContent;
  if (label && /card number/i.test(label.textContent || '')) label.textContent='Payment details';
  const element = elements.create('payment',{layout:'tabs',wallets:{link:'never'},
    ...(options.externalBilling ? {fields:{billingDetails:{address:'never'}}} : {}),
  });
  element.mount('#'+ids.cardNumber);
  return {provider:'stripe',
    async token(name='',save=options.save===true,billing={}) {
      const rawCountry=String(billing.countryCode || billing.country || '').trim();
      const country=/^canada$/i.test(rawCountry)?'CA':/^(united states|united states of america|usa)$/i.test(rawCountry)?'US':rawCountry.toUpperCase();
      if(options.externalBilling && !/^[A-Z]{2}$/.test(country))throw Error('Review your billing country before paying.');
      // Align deferred Elements with the server's consent-driven intent options.
      if(options.mode==='payment') elements.update({setupFutureUsage:save?'off_session':null});
      const submitted=await elements.submit();if(submitted.error)throw Error(submitted.error.message || 'Check the payment details.');
      const result=await stripe.createConfirmationToken({elements,params:{payment_method_data:{
        billing_details:{...(name?{name}:{}),...(billing.email?{email:billing.email}:{}),...(billing.phone?{phone:billing.phone}:{}),
          ...(options.externalBilling?{address:{country,line1:billing.line1||'',line2:billing.line2||'',city:billing.city||'',state:billing.region||'',postal_code:billing.postalCode||''}}:{})},
        allow_redisplay:save||options.mode==='setup'?'always':'limited',
      }}});
      if(result.error || !result.confirmationToken?.id)throw Error(result.error?.message || 'Secure payment verification failed.');
      return result.confirmationToken.id;
    },
    destroy(){element.destroy();hidden.forEach(({element,display,priority})=>{if(display)element.style.setProperty('display',display,priority);else element.style.removeProperty('display');});if(styles===null)host.removeAttribute('style');else host.setAttribute('style',styles);if(label&&labelText)label.textContent=labelText;},
  };
}
export function paymentActionOf(value: unknown): StripeAction | null {
  if(!value || typeof value!=='object')return null;
  const root=value as Record<string,unknown>;
  const action=(root['paymentAction'] || (root['payment'] as Record<string,unknown> | undefined)?.['paymentAction']) as StripeAction | undefined;
  return action?.provider==='stripe' && ['authenticate','setup'].includes(action.type)
    && /^(pi|seti)_[A-Za-z0-9]+_secret_[A-Za-z0-9]+$/.test(action.clientSecret)
    && /^pk_(test|live)_/.test(action.publicKey) ? action : null;
}
export async function authenticateStripe(action: StripeAction): Promise<void> {
  await loadStripeScript();
  const stripe=(window as StripeWindow).Stripe!(action.publicKey);
  const result=await stripe.handleNextAction({clientSecret:action.clientSecret});
  if(result.error)throw Error(result.error.message || 'Payment verification was not completed. Check your order before paying again.');
}
// Scoped to explicit checkout requests; never intercept unrelated application fetches.
export async function stripeCheckoutFetch(url: string, options: RequestInit): Promise<Response> {
  const response=await fetch(url,options);
  if(response.status!==409)return response;
  const payload=await response.clone().json().catch(()=>null), action=paymentActionOf(payload);
  if(!action)return response;
  await authenticateStripe(action);
  // Replay the exact order request. Server-side idempotency/reconciliation controls
  // decide whether fulfilment is ready; no new card token or order id is generated.
  for(let i=0;i<8;i++){
    await new Promise(resolve=>setTimeout(resolve,3000));
    const replay=await fetch(url,options);
    if(replay.status!==409)return replay;
    const body=await replay.clone().json().catch(()=>null);
    if(!['PaymentActionRequired','PaymentConfirmationPending','PaymentStatusUnknown','CheckoutInProgress'].includes(body?.error))return replay;
    if(i===7)return replay;
  }
  return response;
}
